import { VariantId } from "shared/hubrise/ids"
import { ICatalog, IVariant } from "shared/hubrise/models"
import { findById, moveItemWithinArray, removeInArrayById, replaceInArrayById } from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"
import * as PriceOverrides from "./PriceOverrides"
import * as Restrictions from "./Restrictions"

/**
 * Add a variant.
 */
export function add(catalog: ICatalog, values: Partial<IVariant>): { newCatalog: ICatalog; variant: IVariant } {
  const id = VariantId(catalog.idGenerator())
  const defaultValues: IVariant = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id,
    ref: "",
    name: "",
  }
  const variant: IVariant = { ...defaultValues, ...values }
  const variants = [...catalog.data.variants, variant]
  const newCatalog: ICatalog = updatedCatalog(catalog, {
    variants,
    products: catalog.data.products.map((product) => ({
      ...product,
      skus: product.skus.map((sku) => ({
        ...sku,
        restrictions: Restrictions.addVariant(variants, sku.restrictions, variant.ref),
      })),
    })),
    optionLists: catalog.data.optionLists.map((optionList) => ({
      ...optionList,
      options: optionList.options.map((option) => ({
        ...option,
        restrictions: Restrictions.addVariant(variants, option.restrictions, variant.ref),
      })),
    })),
    deals: catalog.data.deals.map((deal) => ({
      ...deal,
      restrictions: Restrictions.addVariant(variants, deal.restrictions, variant.ref),
    })),
    discounts: catalog.data.discounts.map((discount) => ({
      ...discount,
      restrictions: Restrictions.addVariant(variants, discount.restrictions, variant.ref),
    })),
    charges: catalog.data.charges.map((charge) => ({
      ...charge,
      restrictions: Restrictions.addVariant(variants, charge.restrictions, variant.ref),
    })),
  })
  return { newCatalog, variant }
}

/**
 * Create an empty variant.
 */
export function create(catalog: ICatalog): IVariant {
  // Use the first number that is not already taken
  const ref = String(
    catalog.data.variants.reduce((acc, variant) => {
      const ref = parseInt(variant.ref)
      return ref > acc ? ref : acc
    }, 0) + 1,
  )

  return {
    id: VariantId(catalog.idGenerator()),
    ref,
    name: "",
  }
}

/**
 * Delete the variant. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param variant
 */
export function doDelete(catalog: ICatalog, variant: IVariant): ICatalog {
  const variants = removeInArrayById(catalog.data.variants, variant.id)
  return updatedCatalog(catalog, {
    variants,
    products: catalog.data.products.map((product) => ({
      ...product,
      skus: product.skus.map((sku) => ({
        ...sku,
        restrictions: Restrictions.removeVariant(variants, sku.restrictions, variant.ref),
        priceOverrides: PriceOverrides.removeVariant(sku.priceOverrides, variant.ref),
      })),
    })),
    optionLists: catalog.data.optionLists.map((optionList) => ({
      ...optionList,
      options: optionList.options.map((option) => ({
        ...option,
        restrictions: Restrictions.removeVariant(variants, option.restrictions, variant.ref),
        priceOverrides: PriceOverrides.removeVariant(option.priceOverrides, variant.ref),
      })),
    })),
    deals: catalog.data.deals.map((deal) => ({
      ...deal,
      restrictions: Restrictions.removeVariant(variants, deal.restrictions, variant.ref),
    })),
    discounts: catalog.data.discounts.map((discount) => ({
      ...discount,
      restrictions: Restrictions.removeVariant(variants, discount.restrictions, variant.ref),
    })),
    charges: catalog.data.charges.map((charge) => ({
      ...charge,
      restrictions: Restrictions.removeVariant(variants, charge.restrictions, variant.ref),
    })),
  })
}

/**
 * Return the variant matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: VariantId): IVariant => {
  const variant = findById(catalog.data.variants, id)
  if (!variant) throw `Variant ${id} not found`
  return variant
}

/**
 * Move a variant to a new position.
 * @param catalog
 * @param variant
 * @param newPosition
 */
export function move(catalog: ICatalog, variant: IVariant, newPosition: number): ICatalog {
  const variants: Array<IVariant> = moveItemWithinArray(catalog.data.variants, variant, newPosition)
  return updatedCatalog(catalog, { variants })
}

/**
 * Return the names of the variants matching the refs. Skip refs that are not found.
 * @param catalog
 * @param refs
 */
export function refsToNames(catalog: ICatalog, refs: Array<string>): Array<string> {
  const names: Array<string> = []
  for (const ref of refs) {
    const variant = catalog.data.variants.find((variant) => variant.ref === ref)
    if (variant) names.push(variant.name)
  }
  return names
}

/**
 * Update a variant and return the resulting catalog.
 * @param catalog
 * @param id
 * @param name
 */
export function update(catalog: ICatalog, id: VariantId, name: string): ICatalog {
  return updatedCatalog(catalog, {
    variants: replaceInArrayById(catalog.data.variants, id, (variant) => ({
      ...variant,
      name,
    })),
  })
}
